import React from "react";
import { useCombobox } from "downshift";
import styled, { css } from "styled-components";
import moment from "moment-timezone";
import { toast } from "react-toastify";

import { formatDate } from "src/library/utils/moment";
import { Text } from "src/library/components/Text";
import {
  Select,
  IFormOption
} from "src/library/components/Forms";
import { InputCss } from "src/library/components/Forms/Input";
import { Button } from "src/library/components/Button";
import {
  CheckboxLabel,
  CheckboxStyled
} from "src/library/components/Forms/Checkbox";
import { Icons } from "src/library/components/Icon/types";
import { PageProps } from "src/pages/Router/routes";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { useSearchApi } from "src/core/shared/api/search.api";
import { ProductSearchComponent } from "src/core/products/components/ProductSearchComponent";
import { IProduct } from "src/core/products/api/products.api";
import { DateInput } from "src/core/shared/components/Date/DateInput";
import { useSubstitutionsApi } from "src/modules/substitutions/api/substitutions.api";
import { SelectedProductCard } from "src/core/products/components/ProductCard";

export const CreateSubstitutionPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { terminology } = props;
  // API
  const [ searchPreviousProductResponse, searchPreviousProductRequest ] = useSearchApi("SEARCH_PRODUCTS");
  const [ searchReplacementProductResponse, searchReplacementProductRequest ] = useSearchApi("SEARCH_PRODUCTS");
  const [ getReasonCodesResponse, getReasonCodesRequest ] = useSubstitutionsApi("GET_REASON_CODES");
  const [ createSubstitutionResponse, createSubstitutionRequest ] = useSubstitutionsApi("CREATE_SUBSTITUTION");
  // state
  const [ previousProductSearchValue, setPreviousProductSearchValue ] = React.useState("");
  const [ previousProduct, setPreviousProduct ] = React.useState<IProduct>();
  const [ replacementProductSearchValue, setReplacementProductSearchValue ] = React.useState("");
  const [ replacementProduct, setReplacementProduct ] = React.useState<IProduct>();
  const [ startDate, setStartDate ] = React.useState<Date>();
  const [ endDate, setEndDate ] = React.useState<Date>();
  const [ reasonCode, setReasonCode ] = React.useState<number>();
  const [ notifyUsers, setNotifyUsers ] = React.useState<boolean>(false);
  const [ message, setMessage ] = React.useState("");

  // unset end date if start date set to after end date
  React.useEffect(() => {
    if (startDate && endDate && startDate > endDate) {
      setEndDate(undefined);
    }
  }, [ startDate, endDate ]);

  React.useEffect(() => {
    getReasonCodesRequest({});
  }, [ getReasonCodesRequest ]);

  const reasonCodeOptions = React.useMemo<IFormOption[]>(() => {
    if (!getReasonCodesResponse.data) {
      return [];
    }

    return getReasonCodesResponse.data.map(reasonCode => ({
      value: reasonCode.id.toString(),
      text: reasonCode.description
    }));
  }, [ getReasonCodesResponse.data ]);

  React.useEffect(() => {
    if (previousProductSearchValue) {
      searchPreviousProductRequest({
        pagination: {
          offset: 0,
          limit: 100,
          page: 0,
          sort: [ [ "description", "DESC" ] ]
        },
        filters: {
          $or: [ { description: { $like: `%${previousProductSearchValue}%` } }, { partNumber: { $like: `%${previousProductSearchValue}%` } } ],
          $not: replacementProduct ? [ { id: replacementProduct.id } ] : undefined
        }
      });
    }
  }, [
    replacementProduct,
    previousProductSearchValue,
    searchPreviousProductRequest
  ]);

  React.useEffect(() => {
    if (replacementProductSearchValue) {
      searchReplacementProductRequest({
        pagination: {
          offset: 0,
          limit: 100,
          page: 0,
          sort: [ [ "description", "DESC" ] ]
        },
        filters: {
          $or: [ { description: { $like: `%${replacementProductSearchValue}%` } }, { partNumber: { $like: `%${replacementProductSearchValue}%` } } ],
          $not: previousProduct ? [ { id: previousProduct.id } ] : undefined
        }
      });
    }
  }, [
    previousProduct,
    replacementProductSearchValue,
    searchReplacementProductRequest
  ]);

  const handleCreateSubstitution = () => {
    if (
      previousProduct &&
      replacementProduct &&
      startDate &&
      endDate &&
      reasonCode
    ) {
      createSubstitutionRequest({
        previousProductId: previousProduct.id,
        replacementProductId: replacementProduct.id,
        startDate: formatDate(startDate, "YYYY-MM-DD"),
        endDate: formatDate(endDate, "YYYY-MM-DD"),
        reasonCodeId: reasonCode,
        message,
        notifyUsers
      });
    }
  };

  React.useEffect(() => {
    if (createSubstitutionResponse.error) {
      toast.error(createSubstitutionResponse.error.message);
    }
  }, [ createSubstitutionResponse.error ]);

  React.useEffect(() => {
    if (createSubstitutionResponse.data) {
      toast.success("Substitution created successfully!");
      props.history.push(`/admin/products/substitutions/${createSubstitutionResponse.data.id}`);
    }
  }, [ createSubstitutionResponse.data, props.history ]);

  const previousDownShiftProps = useCombobox<IProduct>({
    items: searchPreviousProductResponse.data ? searchPreviousProductResponse.data.result : [],
    itemToString: () => "",
    onSelectedItemChange: ({ selectedItem }) => {
      setPreviousProductSearchValue("");
      setPreviousProduct(selectedItem || undefined);
    }
  });

  const replacementDownShiftProps = useCombobox<IProduct>({
    items: searchReplacementProductResponse.data ? searchReplacementProductResponse.data.result : [],
    itemToString: () => "",
    onSelectedItemChange: ({ selectedItem }) => {
      setReplacementProductSearchValue("");
      setReplacementProduct(selectedItem || undefined);
    }
  });

  return (
    <Layout {...props}>
      <StyledContentWrapper fill>
        <LabelText>
          {`Which ${terminology.product.singular.text} is unavailable?`}
        </LabelText>

        {previousProduct ? (
          <SelectedProductCard
            product={previousProduct}
            icon={Icons.CLOSE}
            onClick={() => setPreviousProduct(undefined)}
          />
        ) : (
          <ProductSearchComponent
            error={searchPreviousProductResponse.error}
            onSearch={setPreviousProductSearchValue}
            loading={searchPreviousProductResponse.pending}
            searchResults={searchPreviousProductResponse.data ? searchPreviousProductResponse.data.result : []}
            downshiftProps={previousDownShiftProps}
            placeholder={`Search for a ${terminology.product.singular.text}`}
          />
        )}

        <LabelText>
          {`Which ${terminology.product.singular.text} should replace it?`}
        </LabelText>

        {replacementProduct ? (
          <SelectedProductCard
            product={replacementProduct}
            icon={Icons.CLOSE}
            onClick={() => setReplacementProduct(undefined)}
          />
        ) : (
          <ProductSearchComponent
            error={searchReplacementProductResponse.error}
            onSearch={setReplacementProductSearchValue}
            loading={searchReplacementProductResponse.pending}
            searchResults={searchReplacementProductResponse.data ? searchReplacementProductResponse.data.result : []}
            downshiftProps={replacementDownShiftProps}
            placeholder={`Search for a ${terminology.product.singular.text}`}
          />
        )}

        <LabelText>
          How long for?
        </LabelText>

        <DateRangeWrapper>
          <DateInput
            label="Start Date"
            date={startDate}
            setDate={setStartDate}
            minimumDate={new Date()}
          />

          <DateInput
            label="End Date"
            date={endDate}
            setDate={setEndDate}
            minimumDate={startDate || new Date()}
            maximumDate={moment.utc().add(2, "years").toDate()}
          />
        </DateRangeWrapper>

        <LabelText>
          Please specify a reason
        </LabelText>

        <SelectWrapper>
          <Select
            as="select"
            label=""
            name="reason-select"
            defaultValue="-"
            unselectedOption={{
              value: "-",
              text: "Reason code",
              disabled: true
            }}
            options={reasonCodeOptions}
            handleChange={e => setReasonCode(parseInt(e.target.value))}
          />
        </SelectWrapper>

        <StyledTextArea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Customer message"
          rows={8}
        />

        <CheckboxLabel
          className="notify-users-wrapper"
          for="notifyUsers"
          label="Send users a notifcation to warn them of the substitution change"
        >
          <CheckboxStyled
            id="notifyUsers"
            name="notifyUsers"
            checked={notifyUsers}
            onChange={event => {
              // Update the emailNotificationsState
              setNotifyUsers(event.target.checked);
            }}
          />
        </CheckboxLabel>

        <StyledButton
          onClick={handleCreateSubstitution}
          loading={createSubstitutionResponse.pending}
          disabled={!(
            previousProduct &&
            replacementProduct &&
            startDate &&
            endDate &&
            reasonCode &&
            message
          )}
        >
          Create Substitution
        </StyledButton>
      </StyledContentWrapper>
    </Layout>
  );
};

const StyledContentWrapper = styled(MainContentWrapper)`
${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    .notify-users-wrapper {
      margin-top: ${theme.spacing.margins.md};
    }
  `}
`;

const LabelText = styled(Text).attrs({ color: "secondary" })`
  margin-bottom: 1rem;
  &:not(:first-child) {
    margin-top: 3rem;
  }
`;

const DateRangeWrapper = styled.div`
  display: flex;
  flex-direction: row;

  & > * + * {
    margin-left: 2rem;
  }
`;

const SelectWrapper = styled.div`
  width: 320px;
`;

const StyledTextArea = styled.textarea`
  ${InputCss}
  border-color: ${({ theme }) => theme.colors.lightGrey};
  border-radius: ${({ theme }) => theme.borderRadius};
  max-width: 640px;
  margin-top: 3rem;
`;

const StyledButton = styled(Button)`
  margin-top: 3rem;
`;
